import { useAuthStore } from '../../state_management_and_stores/auth.store';
import { UserStatsCards } from '../../components/dashboard/UserStatsCards';
import { UpcomingEventsSection } from '../../components/dashboard/UpcomingEventsSection';
import { BrowseUpcomingSection } from '../../components/dashboard/BrowseUpcomingSection';
import { EventHistorySection } from '../../components/dashboard/EventHistorySection';
import { DrawnOutWarningCard } from '../../components/dashboard/DrawnOutWarningCard';
import { PendingInvitationsCard } from '../../components/dashboard/PendingInvitationsCard';
import { BuddyGroupPendingInvitationsCard } from '../../components/dashboard/BuddyGroupPendingInvitationsCard';
import { EntryCloseBanner } from '../../components/dashboard/EntryCloseBanner';
import { NeedsPartnerBanner } from '../../components/dashboard/NeedsPartnerBanner';

export default function DashboardPage() {
  const { profile, role } = useAuthStore();
  const firstName = profile?.first_name || (profile as any)?.display_first_name || '';
  const displayName = firstName || (profile as any)?.full_name || (profile as any)?.email || 'Cowboy';
  const roleName = role?.role_name || role?.role_type || 'user';
  // Combined dashboard: admins/delegates see the same landing page as
  // contestants, plus their additional operational reminders (currently
  // the drawn-out warning card). The card calls a backend endpoint that
  // is already gated by requireAdmin; non-admin requests would 403, so
  // we only mount it when the role is one we expect to be admitted.
  // (The card also self-hides when there's nothing to act on.)
  const showAdminReminders =
    roleName === 'admin' || roleName === 'delegate';

  return (
    <div className="space-y-6">
      {/* Entry-close banner — sits ABOVE the welcome card so the live
          countdown is the very first thing the user sees when a rodeo
          they could enter is within the 3-day window. Self-hides when
          nothing's closing soon, so the dashboard stays calm in the
          common case. */}
      <EntryCloseBanner />

      {/* Welcome card */}
      <div className="rounded-xl border overflow-hidden">
        <div className="h-1 bg-primary" />
        <div className="p-6">
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome back, {displayName}!
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            You're signed in as{' '}
            <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary capitalize">
              {roleName}
            </span>
          </p>
        </div>
      </div>

      {/* Needs-a-partner banner: same urgency tier as pending invitations
          but the action item is on the team primary, not the invitee. The
          previous partner declined and the entry sits in payment-hold
          limbo (status=pending_confirmation_and_payment, draw_eligible=
          false) until the primary either re-invites or cancels. Without
          this banner the entry was easy to scroll past and users showed
          up to events without realising their team had collapsed. */}
      <NeedsPartnerBanner />

      {/* Pending event invitations: a true action item that must be
          handled, so it sits ABOVE stats/upcoming/admin reminders so
          users see it the moment they land. The card self-hides when
          there's nothing to act on, so non-team-ropers don't see an
          empty slot. */}
      <PendingInvitationsCard />

      {/* Pending buddy group invitations: same urgency tier as the
          team-roping invite banner above. Sits directly below it so
          when a user has both kinds of invites they read top-to-bottom
          in the same visual block. Self-hides when empty. */}
      <BuddyGroupPendingInvitationsCard />

      {showAdminReminders && <DrawnOutWarningCard />}

      <UserStatsCards />
      <UpcomingEventsSection />
      {/* Two-column upcoming layout: rodeos on the left, jackpots on the
          right. Stacks on mobile so each column gets full width. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BrowseUpcomingSection type="rodeo" />
        <BrowseUpcomingSection type="jackpot" />
      </div>
      <EventHistorySection />
    </div>
  );
}
